import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ThumbsUpDownOutlinedIcon from "@mui/icons-material/ThumbsUpDownOutlined";
import { makeStyles } from "tss-react/mui";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";
import { i18n } from "../../translate/i18n";
import MessageField from "./MessageField";
import RateRulesEditor from "./RateRulesEditor";
import QueueSurveyTable from "./QueueSurveyTable";

const MIN_TIMEOUT = 1;
const MAX_TIMEOUT = 1440;

const t = (key, options) => i18n.t(`settings.attention.survey.${key}`, options);

const useStyles = makeStyles()((theme) => ({
  header: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 2.5, 1.5),
  },
  headerIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 40,
    height: 40,
    flexShrink: 0,
    borderRadius: 10,
    color: theme.palette.primary.contrastText,
    background: theme.palette.primary.main,
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  title: {
    fontSize: 17,
    fontWeight: 700,
  },
  subtitle: {
    fontSize: 12.5,
    color: theme.palette.text.secondary,
  },
  unsaved: {
    height: 22,
    fontSize: 11.5,
    fontWeight: 600,
    color: "#3a2c00",
    background: "#ffb74d",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(1, 2.5, 2),
  },
  panel: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: theme.spacing(0.5, 2),
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 0),
    "& + &": {
      borderTop: `1px solid ${theme.palette.divider}`,
    },
  },
  rowText: {
    flex: 1,
    minWidth: 0,
  },
  rowTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontSize: 13.5,
    fontWeight: 600,
  },
  rowHelp: {
    fontSize: 12.5,
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  badge: {
    height: 20,
    fontSize: 11,
    fontWeight: 600,
  },
  scopeSelect: {
    width: 260,
    flexShrink: 0,
    [theme.breakpoints.down("sm")]: {
      width: 170,
    },
  },
  timeout: {
    width: 120,
    flexShrink: 0,
  },
  fields: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  actions: {
    padding: theme.spacing(1.5, 2.5),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const withKeys = (rules) =>
  (rules || []).map((rule, index) => ({ key: `rule-${index}`, rates: rule.rates || [], message: rule.message || "" }));

const withoutKeys = (rules) => rules.map(({ rates, message }) => ({ rates, message: message.trim() }));

const toFormState = (survey, isQueue) => {
  const config = survey?.config || {};
  return {
    enabled: isQueue ? survey?.ratingEnabled !== false : survey?.mode === "company" || survey?.mode === "queue",
    scope: survey?.mode === "queue" ? "queue" : "company",
    sendOnAutoClose: config.sendOnAutoClose !== false,
    interactiveList: config.interactiveList === true,
    requestMessage: config.requestMessage || "",
    thanksMessage: config.thanksMessage || "",
    askComment: config.askComment === true,
    commentTimeoutMinutes: String(config.commentTimeoutMinutes ?? 15),
    commentRules: withKeys(config.commentRules),
    customThanks: config.customThanks === true,
    thanksRules: withKeys(config.thanksRules),
  };
};

const snapshotOf = (form) =>
  JSON.stringify({
    ...form,
    commentRules: withoutKeys(form.commentRules),
    thanksRules: withoutKeys(form.thanksRules),
  });

const rulesAreValid = (rules) => rules.every((rule) => rule.rates.length > 0 && rule.message.trim() !== "");

const SatisfactionSurveyModal = ({ open, onClose, survey, onSaved, queue }) => {
  const { classes } = useStyles();
  const isQueue = Boolean(queue);
  const [form, setForm] = useState(() => toFormState(survey, isQueue));
  const [initialSnapshot, setInitialSnapshot] = useState(() => snapshotOf(toFormState(survey, isQueue)));
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingQueue, setEditingQueue] = useState(null);
  const [queuesRefreshKey, setQueuesRefreshKey] = useState(0);

  useEffect(() => {
    if (!open) return;
    const next = toFormState(survey, isQueue);
    setForm(next);
    setInitialSnapshot(snapshotOf(next));
    setShowErrors(false);
  }, [open, survey, isQueue]);

  const dirty = useMemo(() => snapshotOf(form) !== initialSnapshot, [form, initialSnapshot]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const perDepartment = !isQueue && form.scope === "queue";
  const timeout = Number(form.commentTimeoutMinutes);
  const errors = perDepartment
    ? {}
    : {
        requestMessage: !form.requestMessage.trim(),
        thanksMessage: !form.thanksMessage.trim(),
        commentTimeout:
          form.askComment && (!Number.isInteger(timeout) || timeout < MIN_TIMEOUT || timeout > MAX_TIMEOUT),
        commentRules: form.askComment && !rulesAreValid(form.commentRules),
        thanksRules: form.customThanks && !rulesAreValid(form.thanksRules),
      };

  const hasErrors = Object.values(errors).some(Boolean);

  const toggleAskComment = (checked) =>
    set({
      askComment: checked,
      commentRules:
        checked && form.commentRules.length === 0
          ? [{ key: `rule-${Date.now()}`, rates: [4, 5], message: "" }]
          : form.commentRules,
    });

  const toggleCustomThanks = (checked) =>
    set({
      customThanks: checked,
      thanksRules:
        checked && form.thanksRules.length === 0
          ? [{ key: `rule-${Date.now()}`, rates: [4, 5], message: "" }]
          : form.thanksRules,
    });

  const handleSave = async () => {
    if (hasErrors) {
      setShowErrors(true);
      toast.error(t("fixErrors"));
      return;
    }
    setSaving(true);
    try {
      const config = {
          sendOnAutoClose: form.sendOnAutoClose,
          interactiveList: form.interactiveList,
          requestMessage: form.requestMessage.trim(),
          thanksMessage: form.thanksMessage.trim(),
          askComment: form.askComment,
          commentTimeoutMinutes: timeout,
          commentRules: withoutKeys(form.commentRules),
          customThanks: form.customThanks,
          thanksRules: withoutKeys(form.thanksRules),
      };
      const { data } = isQueue
        ? await api.put(`/satisfaction-survey/queue/${queue.id}`, { ratingEnabled: form.enabled, config })
        : await api.put("/satisfaction-survey", { mode: form.enabled ? form.scope : "disabled", config });
      toast.success(t("saved"));
      onSaved(data);
      onClose();
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  const renderSwitchRow = (key, checked, onChange, disabled = false) => (
    <div className={classes.row}>
      <div className={classes.rowText}>
        <Typography className={classes.rowTitle}>{t(`${key}.title`)}</Typography>
        <Typography className={classes.rowHelp}>{t(`${key}.help`)}</Typography>
      </div>
      <Switch checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
    </div>
  );

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth scroll="paper">
      <div className={classes.header}>
        <div className={classes.headerIcon}>
          <ThumbsUpDownOutlinedIcon />
        </div>
        <div className={classes.headerText}>
          <div className={classes.titleRow}>
            <Typography className={classes.title}>{t("modalTitle")}</Typography>
            {dirty && <Chip size="small" label={t("unsaved")} className={classes.unsaved} />}
          </div>
          <Typography className={classes.subtitle}>
            {isQueue ? t("queueSubtitle", { name: queue.name }) : t("modalSubtitle")}
          </Typography>
        </div>
        <IconButton size="small" onClick={onClose} disabled={saving}>
          <CloseIcon />
        </IconButton>
      </div>

      <DialogContent dividers className={classes.content}>
        {isQueue ? (
          <div className={classes.panel}>{renderSwitchRow("queueEnabled", form.enabled, (enabled) => set({ enabled }))}</div>
        ) : (
          <div className={classes.panel}>
            {renderSwitchRow("enabled", form.enabled, (enabled) => set({ enabled }))}
            <div className={classes.row}>
              <div className={classes.rowText}>
                <Typography className={classes.rowTitle}>{t("scope.title")}</Typography>
                <Typography className={classes.rowHelp}>{t("scope.help")}</Typography>
              </div>
              <TextField
                select
                size="small"
                className={classes.scopeSelect}
                value={form.scope}
                disabled={!form.enabled}
                onChange={(e) => set({ scope: e.target.value })}
              >
                <MenuItem value="company">{t("scope.company")}</MenuItem>
                <MenuItem value="queue">{t("scope.queue")}</MenuItem>
              </TextField>
            </div>
          </div>
        )}

        {perDepartment && (
          <>
            <Alert severity="info" variant="outlined">
              {t("perDepartmentHint")}
            </Alert>
            <QueueSurveyTable refreshKey={queuesRefreshKey} onConfigure={setEditingQueue} />
          </>
        )}

        {!perDepartment && (
          <div className={classes.fields}>
            <div className={classes.panel}>
              {renderSwitchRow("autoClose", form.sendOnAutoClose, (sendOnAutoClose) => set({ sendOnAutoClose }))}
              {renderSwitchRow("interactiveList", form.interactiveList, (interactiveList) => set({ interactiveList }))}
            </div>

            <MessageField
              label={t("requestMessage")}
              value={form.requestMessage}
              onChange={(requestMessage) => set({ requestMessage })}
              error={showErrors && errors.requestMessage}
              helperText={showErrors && errors.requestMessage ? t("required") : t("requestHelp")}
            />

            <MessageField
              label={t("thanksMessage")}
              value={form.thanksMessage}
              onChange={(thanksMessage) => set({ thanksMessage })}
              error={showErrors && errors.thanksMessage}
              helperText={
                showErrors && errors.thanksMessage ? t("required") : t("variablesHint", { name: "{{name}}" })
              }
            />

            <div className={classes.panel}>
              {renderSwitchRow("askComment", form.askComment, toggleAskComment)}
            </div>

            {form.askComment && (
              <>
                <div className={classes.panel}>
                  <div className={classes.row}>
                    <div className={classes.rowText}>
                      <Typography className={classes.rowTitle}>
                        {t("commentTimeout.title")}
                        <Chip size="small" variant="outlined" color="primary" label={t("commentTimeout.badge")} className={classes.badge} />
                      </Typography>
                      <Typography className={classes.rowHelp}>{t("commentTimeout.help")}</Typography>
                    </div>
                    <TextField
                      size="small"
                      type="number"
                      className={classes.timeout}
                      value={form.commentTimeoutMinutes}
                      error={showErrors && errors.commentTimeout}
                      onChange={(e) => set({ commentTimeoutMinutes: e.target.value })}
                      inputProps={{ min: MIN_TIMEOUT, max: MAX_TIMEOUT }}
                      InputProps={{ endAdornment: <InputAdornment position="end">{t("commentTimeout.unit")}</InputAdornment> }}
                    />
                  </div>
                </div>
                <RateRulesEditor
                  rules={form.commentRules}
                  onChange={(commentRules) => set({ commentRules })}
                  messageLabel={t("commentMessage")}
                  uncoveredLabel={t("noComment")}
                  showErrors={showErrors}
                />
              </>
            )}

            <div className={classes.panel}>
              {renderSwitchRow("customThanks", form.customThanks, toggleCustomThanks)}
            </div>

            {form.customThanks && (
              <RateRulesEditor
                rules={form.thanksRules}
                onChange={(thanksRules) => set({ thanksRules })}
                messageLabel={t("customMessage")}
                uncoveredLabel={t("defaultThanks")}
                showErrors={showErrors}
              />
            )}
          </div>
        )}
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button onClick={onClose} disabled={saving}>
          {t("cancel")}
        </Button>
        <Box position="relative">
          <Button
            variant="contained"
            startIcon={<CheckIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {t("save")}
          </Button>
          {saving && (
            <CircularProgress size={22} sx={{ position: "absolute", top: "50%", left: "50%", mt: "-11px", ml: "-11px" }} />
          )}
        </Box>
      </DialogActions>
      {!isQueue && (
        <SatisfactionSurveyModal
          open={Boolean(editingQueue)}
          queue={editingQueue}
          survey={editingQueue}
          onClose={() => setEditingQueue(null)}
          onSaved={() => setQueuesRefreshKey((key) => key + 1)}
        />
      )}
    </Dialog>
  );
};

export default SatisfactionSurveyModal;
